import { useState, useMemo } from "react";

import Button from "components/Button";
import { parseBullhornMetadata } from "utils/helpers/bullhorn";
import {
  Table,
  TableBody,
  TableHeader,
  TableRow,
  TableHead,
  TableCell,
} from "components/ui/table";
import { cn } from "utils";

const metadataListClassName =
  "list-none space-y-1 font-inter text-base font-normal text-slate-700 [&>li]:relative [&>li]:pl-2 [&>li]:before:absolute [&>li]:before:left-0 [&>li]:before:top-[0.55em] [&>li]:before:h-1.5 [&>li]:before:w-1.5 [&>li]:before:rounded-full [&>li]:before:bg-gray-500";

function BullhornMetadata({ metadata, borderTop, showTitle, className, type }) {
  const [isHidden, setIsHidden] = useState(true);

  return (
    <div className={cn("mb-4 pb-2", className, { "mt-4": !showTitle })}>
      {borderTop && <hr className="mb-4 w-full border-t-2 border-gray-300" />}
      {showTitle && (
        <div className="flex items-center justify-between">
          <p className="font-inter text-base font-medium text-slate-700">
            Bullhorn Metadata
          </p>
          <Button
            onClick={() => {
              setIsHidden((current) => !current);
            }}
            className="block text-sm font-normal md:hidden"
          >
            {isHidden ? "Open" : "Close"}
          </Button>
        </div>
      )}
      <Metadata unparsedMetadata={metadata} hidden={isHidden} type={type} />
    </div>
  );
}

function Metadata({ unparsedMetadata, hidden, type }) {
  const metadata = useMemo(
    () => parseBullhornMetadata(unparsedMetadata),
    [unparsedMetadata]
  );

  if (type === "table") {
    return <MetadataTable metadata={metadata} />;
  }

  if (!metadata || metadata.length === 0) {
    return (
      <p
        className={cn("mb-4 font-inter text-base font-normal text-slate-700", {
          "hidden md:block": hidden,
        })}
      >
        No bullhorn metadata provided
      </p>
    );
  }

  return (
    <ul
      className={cn(metadataListClassName, "mt-2", {
        "hidden md:block": hidden,
      })}
    >
      {metadata.map(({ key, value }) => (
        <li key={key}>
          {key}: {value}
        </li>
      ))}
    </ul>
  );
}

function MetadataTable({ metadata }) {
  return (
    <section className="max-h-96 h-96 overflow-y-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Key</TableHead>
            <TableHead>Value</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {metadata.map((item) => (
            <TableRow key={item.key}>
              <TableCell>{item.key}</TableCell>
              <TableCell>
                <pre className="w-full overflow-x-auto whitespace-pre-wrap font-rubik">
                  {item.value}
                </pre>
              </TableCell>
            </TableRow>
          ))}
          {metadata.length === 0 && (
            <TableRow>
              <TableCell colSpan={4} className="text-center">
                No bullhorn metadata provided
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </section>
  );
}

export default BullhornMetadata;
